import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Clock, Mail, Menu, Phone, X } from 'lucide-react';

const NAV_ITEMS = {
  left: [
    { title: 'HOME', path: '/' },
    { title: 'CEMETERIES', path: '/cemeteries' },
    { title: 'SERVICES', path: '/services' },
  ],
  right: [
    { title: 'WHO WE ARE', path: '/about' },
    { title: 'OUR PARTNERS', path: '/partners' },
    { title: 'CONTACT US', path: '/contact' },
  ],
};

const linkClasses = ({ isActive }) =>
  [
    'relative py-1 font-montserrat text-xs font-medium tracking-[0.18em] transition-colors duration-200',
    'after:absolute after:inset-x-0 after:-bottom-0.5 after:h-px after:origin-center after:scale-x-0',
    'after:bg-navy-700 after:transition-transform after:duration-300 hover:after:scale-x-100',
    isActive ? 'text-navy-900 after:scale-x-100' : 'text-slate-500 hover:text-navy-800',
  ].join(' ');

const mobileLinkClasses = ({ isActive }) =>
  [
    'rounded-lg px-3 py-2.5 font-montserrat text-xs font-medium tracking-[0.18em] transition-colors',
    isActive ? 'bg-navy-50 text-navy-900' : 'text-slate-600 hover:bg-slate-50 hover:text-navy-800',
  ].join(' ');

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the mobile menu on navigation — it used to stay open behind the new page.
  useEffect(() => setIsOpen(false), [pathname]);

  const renderNavItems = (items, className = linkClasses) =>
    items.map((item) => (
      <NavLink key={item.path} to={item.path} end={item.path === '/'} className={className}>
        {item.title}
      </NavLink>
    ));

  return (
    <header className="relative z-50">
      {/* Utility bar — deep navy drawn from the logo, so the very first strip of
          the page already speaks the brand colour. */}
      <div className="bg-navy-900 py-2 text-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1 text-xs sm:justify-between">
            <span className="hidden items-center gap-1.5 font-montserrat tracking-wide text-navy-100/90 sm:flex">
              <Clock className="h-3 w-3" />
              Available 24 hours, 7 days a week
            </span>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-1">
              <a
                href="tel:8003700969"
                className="flex items-center gap-1.5 text-navy-100 transition-colors hover:text-white"
              >
                <Phone className="h-3 w-3" />
                (800) 370-0969
              </a>
              <a
                href="mailto:woollsjack@cemeteryburialsolutions.org"
                className="flex items-center gap-1.5 text-navy-100 transition-colors hover:text-white"
              >
                <Mail className="h-3 w-3" />
                woollsjack@cemeteryburialsolutions.org
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation — a hair of translucency + a soft cool shadow lets the
          bar feel lifted above the page without going fully sticky (the cemetery
          detail page relies on top:0 for its own tab bar). */}
      <nav className="border-b border-slate-200 bg-white/95 shadow-[0_1px_0_rgba(8,45,104,0.04),0_10px_30px_-24px_rgba(8,45,104,0.35)] backdrop-blur">
        <div className="container mx-auto px-4">
          <div className="flex h-24 items-center justify-between">
            <div className="hidden flex-1 items-center justify-end gap-8 md:flex">
              {renderNavItems(NAV_ITEMS.left)}
            </div>

            <Link
              to="/"
              className="mx-10 shrink-0 transition-transform duration-300 hover:scale-[1.03]"
              aria-label="Cemetery & Burial Solutions — home"
            >
              <img
                src="/images/navbar/logo.png"
                alt="Cemetery & Burial Solutions"
                className="h-16 w-auto"
              />
            </Link>

            <div className="hidden flex-1 items-center gap-8 md:flex">
              {renderNavItems(NAV_ITEMS.right)}
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className="rounded-lg p-1.5 text-navy-800 transition hover:bg-navy-50 md:hidden"
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isOpen}
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {isOpen && (
            <div className="border-t border-slate-200 py-4 md:hidden">
              <div className="flex flex-col gap-1 px-1">
                {renderNavItems([...NAV_ITEMS.left, ...NAV_ITEMS.right], mobileLinkClasses)}
              </div>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
};

export default Navbar;
